import { forwardRef, useEffect, useRef } from 'react'
import DOMPurify from 'isomorphic-dompurify'

import useCombinedRef from '@/hooks/useCombinedRef'

const HTMLRender = forwardRef(({ tag, htmlString, className = "html-render" }, ref) => {
  const innerRef = useRef(null);
  const refContainer = useCombinedRef(ref, innerRef);
  const Tag = tag || "span";

  const sanitize = (str) => {
    return DOMPurify.sanitize(str, {
      ADD_TAGS: ["iframe"], // whitelist
      ADD_ATTR: ["title", "src", "width", "height", "frameborder", "allowfullscreen"],
    })
  };

  DOMPurify.addHook("uponSanitizeElement", (node, data) => {
    // sanitize iframe
    if (data.tagName === "iframe") {
      const src = node.getAttribute("src") || "";
      // only allow youtube embed
      if (!src.startsWith("https://www.youtube.com/embed/")) {
        return node.parentNode.removeChild(node);
      }
    }
  });

  const html = () => ({
    __html: sanitize(htmlString)
  })

  useEffect(() => {
    if (refContainer && refContainer.current) {
      // - Tables
      const tables = refContainer.current.getElementsByTagName("TABLE")
      const tableLength = (tables.length || 0);

      for(let i = 0; i < tableLength; i++) {
        // Wrap table so its becoming responsive in mobile view
        const table = refContainer.current.getElementsByTagName("TABLE")[i].cloneNode(true)
        const div = document.createElement("div")
        div.classList.add('overflow-x-auto')
        div.appendChild(table)

        refContainer.current.getElementsByTagName("TABLE")[i].replaceWith(div)
      }


      // - iFrames
      const iFrames = refContainer.current.getElementsByTagName("IFRAME");
      const iFrameLength = (iFrames.length || 0);
      for(let i = 0; i < iFrameLength; i++) {
        const iframe = iFrames[i].cloneNode(true)
        const src = iframe.getAttribute("src") || "";

        // Responsive YouTube embed 
        if (src.startsWith("https://www.youtube.com/embed/")) {
          const div = document.createElement("div")
          div.classList.add('aspect-w-16', "aspect-h-9");

          div.appendChild(iframe)
  
          refContainer.current.getElementsByTagName("IFRAME")[i].replaceWith(div)
        }
      }

      // - Anchors
      const anchors = refContainer.current.getElementsByTagName("A")
      if (anchors && anchors.length) {
        for(let i = 0; i < anchors.length; i++) {
          const anchor = anchors[i];
          const anchorParentNode = anchor.parentNode;
          const href = anchor.getAttribute("href") || "";

          const headingTags = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];

          if (href.startsWith("#")) {
            if (headingTags.includes(anchorParentNode.tagName)) {
              // target element for scroll
              const newAnchorParent = anchorParentNode.cloneNode(true);
              newAnchorParent.setAttribute("id", href.replace("#", ""));
              
              const span = document.createElement("span");
              span.innerText = anchor.innerText;
              
              // replace parent node
              refContainer.current.getElementsByTagName("A")[i].parentNode.replaceWith(newAnchorParent);
              // replace anchor with span
              refContainer.current.getElementsByTagName("A")[i].replaceWith(span);
            } else {
              // add on click listener
              refContainer.current.getElementsByTagName("A")[i].onclick = handleOnClickAnchor
            }
          }
        }
      }

    }
  }, [refContainer])

  const handleOnClickAnchor = (event) => {
    if (!event) {
      return null;
    }

    const anchor = event.srcElement;
    const hash = anchor.hash;

    if (hash) {
      const targetEl = document.getElementById(hash.replace("#", ""));
      if (targetEl) {
        // event.preventDefault();
        // targetEl.scrollIntoView();
        window.scrollTo({
          top: targetEl.offsetTop - 200,
          behavior: 'smooth'
        });
      }
    }
  }

  return (
    <>
      {
        htmlString &&
        <Tag
          ref={refContainer}
          className={className}
          dangerouslySetInnerHTML={html()}
        />
      }
    </>
  )
})

HTMLRender.displayName = "HTMLRender";

export default HTMLRender